import {
  useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import usePageMeta from '../hooks/usePageMeta';
import { useSocket } from '../context/SocketContext';
import { AuthContext } from '../context/AuthContext';
import GameEngine from '../game/GameEngine';
import ChatPanel from '../components/game/ChatPanel';
import WhosHere from '../components/game/WhosHere';
import NpcRecorder from '../components/game/NpcRecorder';
import DialoguePanel from '../components/game/DialoguePanel';
import WelcomeBack from '../components/game/WelcomeBack';
import Minimap from '../components/game/Minimap';
import Legend from '../components/game/Legend';
import Wallet from '../components/game/Wallet';
import { matches } from '../game/shortcuts';
import { isAdminAccount } from '../utils/permissions';
import { createNpc } from '../services/npcs';
import {
  JOIN, WELCOME, SNAPSHOT, INPUT, ROOMS, ROSTER, CHAT, NPC_SAY, INTERACT, DIALOGUE, CLAIMED,
  CLOSURES, WELCOME_BACK, COIN_TAKEN, COIN_SPAWNED, COINS, BALANCE,
  OVERVIEW, OVERVIEW_SUB,
  VIEW_WIDTH, VIEW_HEIGHT, VIEW_SCALE,
} from '../game/events';

/** Scrollback depth. Old messages are dropped rather than growing forever. */
const CHAT_HISTORY = 50;

/**
 * The game screen.
 *
 * ARCHITECTURE — read before adding to this file.
 *
 * React owns routing, the auth gate, and any HUD/menu overlay. It does NOT
 * render game entities. The engine is imperative vanilla JS that owns the
 * canvas and its own requestAnimationFrame loop, mounted once here and torn
 * down on unmount. Nothing inside the loop may trigger a React re-render —
 * reconciling at 60fps is what makes canvas games in React janky.
 *
 * Snapshots arrive 20x/sec and are handed straight to the engine. They are
 * deliberately never React state.
 */
function Play() {
  const canvasRef = useRef(null);
  // Separate full-resolution layer for labels. See the comment on the canvases.
  const textCanvasRef = useRef(null);
  const engineRef = useRef(null);
  const {
    connected, error, emit, subscribe,
  } = useSocket();

  // Map list and current map ARE React state — they change on join/leave, not
  // per frame, and they drive DOM. Snapshots are the opposite and never are.
  const [maps, setMaps] = useState([]);
  const [currentMap, setCurrentMap] = useState(null);
  // Chat IS React state, unlike snapshots — it arrives occasionally and drives
  // DOM. The no-React rule is about the 20Hz tick, not about everything.
  const [messages, setMessages] = useState([]);
  const messageSeq = useRef(0);
  // The roster goes to BOTH the engine (which decodes short ids while drawing)
  // and React (which lists who is here). Same data, two consumers with very
  // different update rates — the engine reads it 60 times a second, this
  // re-renders only when someone joins or leaves.
  const [roster, setRoster] = useState([]);
  const [localShortId, setLocalShortId] = useState(null);
  // The open conversation, or null. React state because it drives DOM and
  // changes only when somebody presses a key — nothing like a snapshot.
  const [dialogue, setDialogue] = useState(null);
  /**
   * The same value, readable from inside the engine's callbacks.
   *
   * The engine is constructed once in an effect that must not re-run — tearing
   * it down and rebuilding it on every conversation would drop the canvas and
   * the snapshot buffer. So its callbacks cannot close over `dialogue`; they
   * would capture `null` forever and the "do not stack a second conversation"
   * guard would stop working after the first one.
   */
  const dialogueRef = useRef(null);
  useEffect(() => { dialogueRef.current = dialogue; }, [dialogue]);
  /**
   * The minimap: off, a corner map, or a large one.
   *
   * The map data itself is kept because WELCOME already carries it — the walls
   * cost nothing extra. `overview` is the opt-in feed of where everybody is,
   * which is the only thing here that ignores interest management.
   */
  const [mapView, setMapView] = useState('off');
  const [mapData, setMapData] = useState(null);
  /**
   * What changed while you were away, or null.
   *
   * Null is the ordinary case — the server sends nothing for a first visit, a
   * refresh, or an absence during which nobody did anything. Arrives at most
   * once per connection, so dismissing it is final until the next visit.
   */
  const [awaySummary, setAwaySummary] = useState(null);
  /**
   * What you have, or null until the server has read it.
   *
   * NULL RATHER THAN 0 as the initial value, deliberately — `Wallet` renders
   * nothing on null, so an empty purse never flashes in front of a real
   * balance. See the note in game/balances.js.
   */
  const [balance, setBalance] = useState(null);
  const [overview, setOverview] = useState(null);
  const [legendOpen, setLegendOpen] = useState(false);
  const { user, token } = useContext(AuthContext);

  /**
   * Where the local player is standing, for the NPC route recorder.
   *
   * Reads the engine on demand rather than mirroring position into React
   * state: position changes 20 times a second and would re-render the whole
   * screen for a value only read when a key is pressed. The engine converts to
   * foot space — see `getLocalFootPosition`.
   */
  const getFootPosition = useCallback(
    () => engineRef.current?.getLocalFootPosition() || null,
    [],
  );

  /**
   * Save a recorded route as a new character.
   *
   * The server reloads live rooms as part of the write, so the NPC appears
   * where you are standing without a refresh — that immediacy is the entire
   * reason routes are recorded in-game rather than typed into a form.
   */
  const saveNpc = useCallback(async ({ name, mapName, waypoints }) => {
    await createNpc(token, {
      name,
      mapName,
      route: { mode: 'pingpong', waypoints },
    });
  }, [token]);

  usePageMeta({ title: 'Play', canonicalPath: '/play' });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const engine = new GameEngine(canvas, {
      // The only thing the client sends: which keys are held. Never a position.
      onInputChange: (held) => emit(INPUT, held),
      // Clicking somebody sends exactly what pressing E sends. Read through a
      // ref rather than closed over, because this effect is set up once and a
      // captured `dialogue` would be stale for the life of the session — the
      // guard would then stop working the moment the first conversation opened.
      onTalk: () => { if (!dialogueRef.current) emit(INTERACT, { nodeId: undefined }); },
      textCanvas: textCanvasRef.current,
    });
    engineRef.current = engine;
    engine.start();

    const unsubscribe = subscribe((event, payload) => {
      if (event === WELCOME) {
        if (payload.error) return; // e.g. an unknown map name
        // Snapshots identify players by a per-room short id, not the user id,
        // so this is how the engine finds itself — and the camera follows it.
        engine.setLocalShortId(payload.shortId);
        setLocalShortId(payload.shortId);
        // The server owns the map and ships it here — Netlify and DigitalOcean
        // share no filesystem, so the client cannot read it from disk.
        // setMap also clears the snapshot buffer, which is what stops players
        // sliding in from their positions in the previous map.
        engine.setMap(payload.map, payload.owners, payload.closed);
        // AFTER setMap, never before — that call clears the coin list, because
        // coin ids are scoped to a world and the room we just left had a
        // different coin 7.
        engine.setCoins(payload.coins);
        setMapData(payload.map);
        setCurrentMap(payload.mapName);
        // Positions from the previous room would be drawn against this map.
        setOverview(null);
        // Short ids are room-scoped, so the old log would attribute messages to
        // whoever inherits those ids here.
        setMessages([]);
        // The balance is deliberately NOT reset. It belongs to the player
        // rather than the room, and blanking it here would make the HUD flicker
        // on every portal.
      } else if (event === CHAT) {
        // Two destinations, deliberately: the engine draws a bubble above the
        // speaker's head, and the panel keeps it so a message read while
        // walking is not lost.
        engine.applyChat(payload);

        const identity = engine.roster.get(payload.i);
        messageSeq.current += 1;
        setMessages((prior) => [...prior, {
          key: messageSeq.current,
          system: Boolean(payload.sys),
          name: identity?.name || 'someone',
          color: identity?.color || '#8899AA',
          text: payload.t,
        }].slice(-CHAT_HISTORY));
      } else if (event === NPC_SAY) {
        // A BUBBLE AND NOTHING ELSE — the missing setMessages is the feature,
        // not an oversight. Ambient NPC chatter in the scrollback would bury
        // the messages players actually sent each other, and unlike those,
        // nothing is lost by letting it expire. This is also why it is a
        // separate event from CHAT rather than a flag on it: see events.js.
        engine.applyChat(payload);
      } else if (event === DIALOGUE) {
        setDialogue({ speaker: payload.name, node: payload.node });
      } else if (event === CLAIMED) {
        // Engine only — the label is drawn on the canvas, so React has nothing
        // to re-render. The confirmation the claimer sees arrives separately as
        // a system CHAT message, which is how every other "that worked" reply
        // in here reaches a player.
        engine.setOwner(payload.zoneId, payload.owner);
      } else if (event === WELCOME_BACK) {
        // React, not the engine: this is a panel with a button, which is
        // exactly what React is for. The rule keeping game entities out of
        // state is about the 20Hz tick, not about everything.
        setAwaySummary(payload);
      } else if (event === CLOSURES) {
        // Engine only, like CLAIMED above — the words are drawn on the canvas,
        // so React has nothing to re-render. The whole set every time, so a
        // dropped message heals on the next one instead of leaving a shop shut.
        engine.setClosures(payload?.closed);
      } else if (event === COIN_TAKEN) {
        // Engine only. The coin leaves the canvas and a "+1" floats over
        // whoever got it — if they are on screen. The picker's own number
        // arrives separately as BALANCE, because a balance is private and this
        // event goes to the whole room.
        engine.takeCoin(payload);
      } else if (event === COIN_SPAWNED) {
        engine.addCoin(payload);
      } else if (event === COINS) {
        // The whole set, replacing whatever was there — an admin turned money
        // on or off. Engine only, like CLOSURES above, and for the same reason:
        // coins are drawn on the canvas, so React has nothing to re-render.
        // An EMPTY array is a real payload; it is what "off" looks like.
        engine.setCoins(payload?.coins);
      } else if (event === BALANCE) {
        // React, like WELCOME_BACK above: a number in a box changes when you
        // pick something up, not twenty times a second, so it is nowhere near
        // the tick the no-re-render rule is about.
        setBalance(payload.coins);
      } else if (event === OVERVIEW) {
        setOverview(payload);
      } else if (event === ROSTER) {
        // Names and colours for the short ids in snapshots. Arrives right
        // after WELCOME and again whenever someone joins or leaves — never per
        // tick, which is the point of splitting it out.
        engine.setRoster(payload);
        setRoster(payload?.players || []);
      } else if (event === SNAPSHOT) {
        engine.applySnapshot(payload);
      } else if (event === ROOMS) {
        setMaps(payload.maps || []);
      }
    });

    return () => {
      unsubscribe();
      engine.stop();
      engineRef.current = null;
    };
  }, [emit, subscribe]);

  // Ask to enter the world only once listeners are attached and the socket is
  // up. Emitting on connect instead would race the subscribe() above and drop
  // the WELCOME reply — which is how the local player id goes missing.
  useEffect(() => {
    if (connected) emit(JOIN);
  }, [connected, emit]);

  // Connection status is real React state, so it re-renders — but only on
  // connect/disconnect, not per frame.
  useEffect(() => {
    engineRef.current?.setConnected(connected);
  }, [connected]);

  const sendChat = useCallback((text) => emit(CHAT, { text }), [emit]);

  /**
   * Talk to whoever we are standing next to.
   *
   * The payload never names a target — the server decides who is in range, the
   * same way it decides everything else about position. All this says is "talk",
   * plus which line of the conversation to hear next.
   */
  const talk = useCallback((nodeId) => emit(INTERACT, { nodeId }), [emit]);

  /**
   * Click the world to walk there.
   *
   * Normalised to 0..1 so the engine never has to know how large the canvas is
   * drawn — it is CSS-scaled to fit the viewport, so raw pixels would be wrong
   * on every screen but one.
   */
  const clickWorld = useCallback((event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    engineRef.current?.clickAt(
      (event.clientX - rect.left) / rect.width,
      (event.clientY - rect.top) / rect.height,
    );
  }, []);

  /** Click the minimap to walk there. Foot-space world coordinates already. */
  const clickMap = useCallback((point) => engineRef.current?.walkTo(point), []);

  /**
   * Let the character get on with their route.
   *
   * Every way a conversation ends funnels through here — Escape, the Goodbye
   * button, and a choice with nowhere to go — so the character starts walking
   * again the moment you are done rather than waiting out a timeout.
   */
  const endDialogue = useCallback(() => {
    setDialogue(null);
    emit(INTERACT, { end: true });
  }, [emit]);

  const chooseAnswer = useCallback((choice) => {
    // A choice with nowhere to go is an authored ending, not a broken link.
    if (!choice.next) { endDialogue(); return; }
    talk(choice.next);
  }, [talk, endDialogue]);

  // Bundled so DialoguePanel takes one prop for "how do I give control back",
  // and cannot end a conversation without also restoring the keyboard.
  const dialogueControls = useMemo(() => ({
    close: endDialogue,
    setInputEnabled: (value) => engineRef.current?.setInputEnabled(value),
  }), [endDialogue]);

  /**
   * E opens a conversation.
   *
   * Bound here rather than in Input.js because it is a one-shot action, not
   * held state — Input.js is deliberately about which keys are DOWN. Guarded
   * against firing while a text field has focus, the same rule Input.js applies,
   * so typing "here" into chat does not start four conversations.
   */
  useEffect(() => {
    const onKeyDown = (event) => {
      const el = document.activeElement;
      const typing = el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
      if (typing) return; // "m" belongs in the message, not on the map

      // Keys come from the shortcuts registry rather than string literals, so
      // the legend and the behaviour cannot drift apart.
      if (matches(event, 'talk') && !dialogue) { talk(undefined); return; }

      if (matches(event, 'legend')) {
        event.preventDefault();
        setLegendOpen((open) => !open);
        return;
      }

      /*
        M is the small map, Shift+M the large one, and each toggles only its
        own size. So M from large drops to corner and Shift+M from corner
        grows — every press does something, and neither can strand you in a
        state the other key cannot leave.

        `matches` checks Shift in both directions, which matters here: the two
        share a code, so ignoring the modifier would fire both.
      */
      const wanted = ['mapCorner', 'mapLarge'].find((id) => matches(event, id));
      if (!wanted) return;
      event.preventDefault();
      const view = wanted === 'mapLarge' ? 'large' : 'corner';
      setMapView((current) => (current === view ? 'off' : view));
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [talk, dialogue]);

  // Escape closes the legend, matching chat and dialogue. Separate from the
  // handler above because that one returns early while typing, and Escape has
  // to work regardless of what has focus.
  useEffect(() => {
    if (!legendOpen) return undefined;
    const onKeyDown = (event) => { if (event.key === 'Escape') setLegendOpen(false); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [legendOpen]);

  /**
   * Ask for the overview only while the map is open.
   *
   * This is the one payload that ignores interest management, so it is the one
   * that is rationed — a closed map costs the server nothing. The cleanup also
   * covers unmount, so leaving the page unsubscribes.
   */
  useEffect(() => {
    const open = mapView !== 'off';
    emit(OVERVIEW_SUB, { on: open });
    if (!open) setOverview(null);
    return () => emit(OVERVIEW_SUB, { on: false });
  }, [mapView, emit]);

  /**
   * Hand the keyboard between the chat box and the game.
   *
   * Without this, typing "was" walks the character and the letters never reach
   * the input — movement keys are bound to `window` and call preventDefault().
   */
  const handleChatFocus = useCallback((focused) => {
    engineRef.current?.setInputEnabled(!focused);
  }, []);

  return (
    <div className="flex min-h-[calc(100vh-5rem)] items-center justify-center bg-gray-950 p-2">
      {/*
        ONE FRAME THAT NEVER SCROLLS, which is the whole point of this layout.

        Everything used to stack below the canvas in document flow — chat, who
        is here, the map picker — and on a laptop that put chat off the bottom
        of the screen. Reading it meant scrolling away from the game, at exactly
        the moment you cannot afford to look away. RuneScape puts the view and
        the chat in one fixed frame; so does this now.

        THE SIZING IS ONE `min()`. 320x180 is exactly 16:9, so capping the width
        by the available HEIGHT times 16/9 makes the frame shrink on a short
        screen instead of pushing the HUD off it. No JavaScript, no resize
        listener, and the engine is untouched: the text layer draws in a fixed
        960x540 space and is CSS-scaled, so labels stay aligned at any size.
      */}
      <div
        className="relative w-full"
        style={{ width: `min(${VIEW_WIDTH * VIEW_SCALE}px, 100%, (100vh - 8rem) * 16 / 9)` }}
      >
        {/*
          TWO STACKED CANVASES, and the split is the whole point.

          The art canvas is 320x180 internally, upscaled 3x — the same numbers
          Pizza Legends uses, putting tiles at 48px and character cells at 96px.
          `imageRendering: pixelated` is what keeps that crisp; without it the
          browser smooths the art to mush.

          Text CANNOT live in that buffer. A 6px font there has no hard edges to
          preserve — the browser antialiases it to grey partial pixels, and the
          upscale then faithfully magnifies the antialiasing into blocks. The
          overlay draws the same labels at the display's real resolution
          instead, so they come out sharp at identical apparent size.
        */}
        {/*
          The click surface for walking. A button rather than a bare canvas so
          it is reachable without a mouse; the canvas itself is not focusable.
        */}
        <canvas
          ref={canvasRef}
          width={VIEW_WIDTH}
          height={VIEW_HEIGHT}
          onClick={clickWorld}
          style={{ imageRendering: 'pixelated' }}
          className="block h-auto w-full cursor-pointer rounded-lg border border-gray-800 shadow-2xl"
        >
          This game requires canvas support.
        </canvas>

        <canvas
          ref={textCanvasRef}
          className="pointer-events-none absolute inset-0 h-full w-full"
          aria-hidden="true"
        />

        {/*
          THE HUD, layered on the game.

          `pointer-events-none` here and `pointer-events-auto` on the pieces
          that need it. Backwards, and either chat cannot be clicked or an
          invisible box swallows every click on the world — the same reason the
          text canvas above sets it.

          React still owns all of this. The rule that keeps entities out of
          React is about the 20Hz tick, not about the HUD.
        */}
        {legendOpen && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center p-4">
            <Legend
              isAdmin={isAdminAccount(user)}
              onClose={() => setLegendOpen(false)}
            />
          </div>
        )}

        <div className="pointer-events-none absolute inset-0 flex flex-col justify-between p-2">
          <div className="flex items-start justify-between gap-2">
            {/*
              The top-left row is your SESSION — are you connected, what do you
              have. The right-hand column is the world's business (who is here,
              where you are), and the bottom-left is already contested by chat,
              dialogue and the welcome-back panel, all of which grow.
            */}
            <div className="flex items-start gap-2">
              <div className="flex items-center gap-2 rounded-lg bg-gray-950/70 px-2 py-1 text-xs backdrop-blur-sm">
                <span
                  className={`inline-block h-2 w-2 rounded-full ${connected ? 'bg-green-500' : 'bg-red-500'}`}
                  aria-hidden="true"
                />
                <span className="text-gray-400">{connected ? 'Connected' : 'Disconnected'}</span>
                {error && <span className="text-red-400">{error}</span>}
                {/*
                Replaces a hand-written key list that was already wrong. A
                button as well as the L key, deliberately: a help overlay you
                can only reach with a key you already know is useless to the
                one person who needs it.
              */}
                <button
                  type="button"
                  onClick={() => setLegendOpen((open) => !open)}
                  className="pointer-events-auto rounded px-1 text-gray-500 hover:text-gray-200"
                  title="Controls (L)"
                >
                  ? controls
                </button>
              </div>

              <Wallet coins={balance} />
            </div>

            <div className="flex flex-col items-end gap-2">
              {mapView !== 'off' && (
                <Minimap
                  map={mapData}
                  overview={overview}
                  localShortId={localShortId}
                  large={mapView === 'large'}
                  onWalkTo={clickMap}
                />
              )}
              <WhosHere players={roster} localShortId={localShortId} />
            </div>
          </div>

          <div className="flex items-end justify-between gap-2">
            <div className="w-full max-w-sm space-y-2">
              {/*
                Above the chat rather than beside it: the bottom-left column is
                where things that are about YOU already live, and the top row is
                the world's business. Dismissed by hand rather than on a timer —
                somebody who tabbed away mid-read should not lose it.
              */}
              <WelcomeBack summary={awaySummary} onDismiss={() => setAwaySummary(null)} />
              {dialogue ? (
                <DialoguePanel
                  speaker={dialogue.speaker}
                  node={dialogue.node}
                  onChoose={chooseAnswer}
                  onClose={dialogueControls}
                />
              ) : (
                <ChatPanel
                  messages={messages}
                  onSend={sendChat}
                  onFocusChange={handleChatFocus}
                />
              )}
            </div>

            <div className="flex flex-col items-end gap-1">
              {/*
                Admin only. Rendering is the ONLY thing decided here — every
                /api/npcs route is behind adminAuth and the server re-validates
                each waypoint on save. `isAdminAccount` is the shared mirror of
                the server's rule; this file used to inline it, which is how
                AdminRoute and Header came to disagree with it.
              */}
              {isAdminAccount(user) && (
                <NpcRecorder
                  getFootPosition={getFootPosition}
                  mapName={currentMap}
                  onSave={saveNpc}
                />
              )}

              {maps.length > 1 && (
                <div className="pointer-events-auto flex items-center gap-1 rounded-lg bg-gray-950/70 px-2 py-1 text-xs backdrop-blur-sm">
                  {maps.map((m) => (
                    <button
                      key={m.mapName}
                      type="button"
                      onClick={() => emit(JOIN, { mapName: m.mapName })}
                      className={`rounded px-2 py-0.5 transition-colors ${
                        m.mapName === currentMap
                          ? 'bg-primary text-navy'
                          : 'text-gray-400 hover:bg-gray-800'
                      }`}
                      title={`${m.players} here`}
                    >
                      {m.mapName.replace('arena-', '')}
                      <span className="ml-1 opacity-70">{m.players}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Play;
